import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { globalSearchApi } from "./content/api";
import { formatErr } from "./api";
import { Search, ArrowRight, LayoutDashboard, FileText, BookOpen, HelpCircle, BookMarked, FlaskConical, Newspaper, ClipboardList, Quote, Syringe, Stethoscope, Microscope, Pill, Calendar, Users, Monitor, ScrollText, Flag, ShieldCheck, Inbox, Plus, X, Video, Download, Image as ImageIcon, Book, MessageSquareQuote, Mic, Home, Menu as MenuIcon, ArrowRightLeft, Mail, Settings as SettingsIcon, Network, Presentation, Calculator, Sparkles, Cpu, Layers, UserCheck, Lock } from "lucide-react";

// The palette exposes three action classes:
//   • Search   — matches entities across every linkable type
//   • Create   — jump to /new for any content type
//   • Navigate — hop to any major admin section
//
// Keyboard: ⌘K / Ctrl+K to open · Esc to close · ↑↓ to move · Enter to run.

const NAV_ACTIONS = [
  { id: "nav-dashboard",     label: "Go to Dashboard",             icon: LayoutDashboard, path: "/admin", keywords: "home overview" },
  { id: "nav-users",         label: "Users & Roles",               icon: Users,           path: "/admin/users", keywords: "team access rbac" },
  { id: "nav-sessions",      label: "Sessions & Devices",          icon: Monitor,         path: "/admin/sessions", keywords: "devices login" },
  { id: "nav-audit",         label: "Audit Log",                   icon: ScrollText,      path: "/admin/audit", keywords: "history activity" },
  { id: "nav-flags",         label: "Feature Flags",               icon: Flag,            path: "/admin/feature-flags", keywords: "toggles" },
  { id: "nav-insights",      label: "Insights · Pending Approval", icon: ShieldCheck,     path: "/admin/insights-pending", keywords: "clinical insights" },
  { id: "nav-insights-private", label: "Insights · Private Admin", icon: Lock,          path: "/admin/insights-private", keywords: "clinical insights private admin" },
  { id: "nav-doctor-accounts",  label: "Doctor Accounts",           icon: UserCheck,      path: "/admin/doctor-accounts", keywords: "doctor portal verify licence physician" },
  { id: "nav-suggestions",   label: "Suggestions Inbox",           icon: Inbox,           path: "/admin/suggestions", keywords: "auto suggest links" },
  // Content collections
  { id: "nav-articles",      label: "Articles",                    icon: FileText,        path: "/admin/content/articles", keywords: "content" },
  { id: "nav-conditions",    label: "Conditions",                  icon: BookOpen,        path: "/admin/content/conditions", keywords: "disease" },
  { id: "nav-faqs",          label: "FAQs",                        icon: HelpCircle,      path: "/admin/content/faqs", keywords: "question" },
  { id: "nav-guidelines",    label: "Guidelines",                  icon: BookMarked,      path: "/admin/content/guidelines", keywords: "acog figo rcog nice" },
  { id: "nav-papers",        label: "Research Papers",             icon: FlaskConical,    path: "/admin/content/research-papers", keywords: "study trial" },
  { id: "nav-updates",       label: "Research Updates",            icon: Newspaper,       path: "/admin/content/research-updates", keywords: "news" },
  { id: "nav-summaries",     label: "Evidence Summaries",          icon: ClipboardList,   path: "/admin/content/evidence-summaries", keywords: "evidence" },
  { id: "nav-references",    label: "References",                  icon: Quote,           path: "/admin/content/references", keywords: "citation doi" },
  // Knowledge graph entities
  { id: "nav-treatments",    label: "Treatments",                  icon: Syringe,         path: "/admin/content/treatments", keywords: "graph therapy" },
  { id: "nav-symptoms",      label: "Symptoms",                    icon: Stethoscope,     path: "/admin/content/symptoms", keywords: "graph signs" },
  { id: "nav-investigations",label: "Investigations",              icon: Microscope,      path: "/admin/content/investigations", keywords: "graph tests labs imaging" },
  { id: "nav-medicines",     label: "Medicines",                   icon: Pill,            path: "/admin/content/medicines", keywords: "graph drugs" },
  { id: "nav-trimesters",    label: "Trimesters",                  icon: Calendar,        path: "/admin/content/trimesters", keywords: "graph pregnancy weeks" },
  // Media library
  { id: "nav-videos",        label: "Videos",                      icon: Video,           path: "/admin/content/videos", keywords: "media watch" },
  { id: "nav-guides",        label: "Patient Guides",              icon: Download,        path: "/admin/content/patient-guides", keywords: "media pdf downloads" },
  { id: "nav-infographics",  label: "Infographics",                icon: ImageIcon,       path: "/admin/content/infographics", keywords: "media visuals graphics" },
  { id: "nav-books",         label: "Books",                       icon: Book,            path: "/admin/content/books", keywords: "media reading" },
  { id: "nav-testimonials",  label: "Testimonials",                icon: MessageSquareQuote, path: "/admin/content/testimonials", keywords: "media stories patients" },
  { id: "nav-media",         label: "Media Appearances",           icon: Mic,             path: "/admin/content/media-appearances", keywords: "media press interviews podcast tv" },
  // M6 · Reserved schemas / AI & Tools
  { id: "nav-teaching-slides", label: "Teaching Slides",           icon: Presentation,    path: "/admin/content/teaching-slides", keywords: "deck slide education audience" },
  { id: "nav-clinical-tools",  label: "Clinical Tools",            icon: Calculator,      path: "/admin/content/clinical-tools", keywords: "calculator checklist screening" },
  { id: "nav-ai-prompts",      label: "AI Prompt Library",         icon: Sparkles,        path: "/admin/content/ai-prompts", keywords: "prompt llm template" },
  { id: "nav-ai-models",       label: "AI Model Registry",         icon: Cpu,             path: "/admin/content/ai-models", keywords: "model provider registry" },
  { id: "nav-graph-explorer",  label: "Graph Explorer",            icon: Network,         path: "/admin/graph-explorer", keywords: "graph visualise neighborhood connections" },
  { id: "nav-derivative-queue", label: "Derivative Queue",         icon: Layers,          path: "/admin/derivative-queue", keywords: "infographic derivative render export" },
  // Site
  { id: "nav-homepage",      label: "Homepage editor",             icon: Home,            path: "/admin/homepage", keywords: "site hero landing" },
  { id: "nav-nav",           label: "Navigation Manager",          icon: MenuIcon,        path: "/admin/navigation", keywords: "site menu nav" },
  { id: "nav-redirects",     label: "Redirect Manager",            icon: ArrowRightLeft,  path: "/admin/redirects", keywords: "site 301 302" },
  { id: "nav-enquiries",     label: "Enquiry Inbox",               icon: Mail,            path: "/admin/enquiries", keywords: "site contact form" },
  { id: "nav-seo",           label: "SEO & Structured Data Validation", icon: Search,     path: "/admin/seo", keywords: "site jsonld canonical og twitter meta" },
  { id: "nav-settings",      label: "Settings",                    icon: SettingsIcon,    path: "/admin/settings", keywords: "admin recipients digest configuration" },
];

const CREATE_ACTIONS = [
  { id: "new-article",       label: "New Article",           path: "/admin/content/articles/new" },
  { id: "new-condition",     label: "New Condition",         path: "/admin/content/conditions/new" },
  { id: "new-faq",           label: "New FAQ",               path: "/admin/content/faqs/new" },
  { id: "new-guideline",     label: "New Guideline",         path: "/admin/content/guidelines/new" },
  { id: "new-paper",         label: "New Research Paper",    path: "/admin/content/research-papers/new" },
  { id: "new-update",        label: "New Research Update",   path: "/admin/content/research-updates/new" },
  { id: "new-summary",       label: "New Evidence Summary",  path: "/admin/content/evidence-summaries/new" },
  { id: "new-reference",     label: "New Reference",         path: "/admin/content/references/new" },
  { id: "new-treatment",     label: "New Treatment",         path: "/admin/content/treatments/new" },
  { id: "new-symptom",       label: "New Symptom",           path: "/admin/content/symptoms/new" },
  { id: "new-investigation", label: "New Investigation",     path: "/admin/content/investigations/new" },
  { id: "new-medicine",      label: "New Medicine",          path: "/admin/content/medicines/new" },
  { id: "new-trimester",     label: "New Trimester",         path: "/admin/content/trimesters/new" },
  { id: "new-video",         label: "New Video",             path: "/admin/content/videos/new" },
  { id: "new-guide",         label: "New Patient Guide",     path: "/admin/content/patient-guides/new" },
  { id: "new-infographic",   label: "New Infographic",       path: "/admin/content/infographics/new" },
  { id: "new-book",          label: "New Book",              path: "/admin/content/books/new" },
  { id: "new-testimonial",   label: "New Testimonial",       path: "/admin/content/testimonials/new" },
  { id: "new-appearance",    label: "New Media Appearance",  path: "/admin/content/media-appearances/new" },
  { id: "new-teaching-slide", label: "New Teaching Slide Deck", path: "/admin/content/teaching-slides/new" },
  { id: "new-clinical-tool", label: "New Clinical Tool",     path: "/admin/content/clinical-tools/new" },
  { id: "new-ai-prompt",     label: "New AI Prompt",         path: "/admin/content/ai-prompts/new" },
  { id: "new-ai-model",      label: "New AI Model",          path: "/admin/content/ai-models/new" },
];

const COLL_TO_TYPE_ROUTE = {
  articles: "articles", conditions: "conditions", faqs: "faqs", guidelines: "guidelines",
  research_papers: "research-papers", research: "research-updates",
  evidence_summaries: "evidence-summaries",
  treatments: "treatments", symptoms: "symptoms", investigations: "investigations",
  medicines: "medicines", trimesters: "trimesters",
  videos: "videos", patient_guides: "patient-guides", infographics: "infographics",
  books: "books", testimonials: "testimonials", media_appearances: "media-appearances",
  teaching_slides: "teaching-slides", clinical_tools: "clinical-tools",
  ai_prompts: "ai-prompts", ai_models: "ai-models",
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [entityHits, setEntityHits] = useState([]);
  const [cursor, setCursor] = useState(0);
  const nav = useNavigate();
  const inputRef = useRef(null);

  // Global ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const combo = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k";
      if (combo) { e.preventDefault(); setOpen(o => !o); }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ(""); setCursor(0); setEntityHits([]);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  // Debounced entity search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setEntityHits([]); return; }
    const t = setTimeout(() => {
      globalSearchApi.query(term, 8).then(setEntityHits).catch(() => setEntityHits([]));
    }, 150);
    return () => clearTimeout(t);
  }, [q, open]);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const matchNav = NAV_ACTIONS.filter(a => !term || a.label.toLowerCase().includes(term) || a.keywords.includes(term));
    const matchCreate = CREATE_ACTIONS.filter(a => {
      if (!term) return true; // show all Create options on empty palette
      const l = a.label.toLowerCase();
      return l.includes(term) || term === "new" || term.startsWith("create") || term.startsWith("new ");
    });
    const flat = [];
    entityHits.forEach(h => flat.push({ kind: "entity", ...h, key: `ent-${h.type}-${h.id}` }));
    matchCreate.slice(0, 12).forEach(a => flat.push({ kind: "create", ...a, key: a.id }));
    matchNav.slice(0, 10).forEach(a => flat.push({ kind: "nav", ...a, key: a.id }));
    return flat;
  }, [q, entityHits]);

  useEffect(() => { if (cursor >= groups.length) setCursor(0); }, [groups, cursor]);

  const run = (item) => {
    if (!item) return;
    setOpen(false);
    if (item.kind === "entity") {
      const route = COLL_TO_TYPE_ROUTE[item.type];
      if (route) nav(`/admin/content/${route}/${item.id}`);
    } else {
      nav(item.path);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => Math.min(c + 1, groups.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); run(groups[cursor]); }
  };

  if (!open) return (
    <button data-testid="cmdk-launcher" onClick={() => setOpen(true)}
            title="Open command palette (⌘K)"
            className="hidden md:flex items-center gap-2 border border-line px-3 py-1.5 text-xs text-stone2 hover:text-navy hover:border-navy transition">
      <Search size={13}/> Search or run…
      <span className="mono text-[9px] uppercase tracking-widest bg-clinical text-stone2 px-1.5 py-0.5 border border-line">⌘K</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="cmdk-overlay">
      <div className="bg-white w-full max-w-2xl border border-line shadow-xl" onClick={e => e.stopPropagation()} data-testid="cmdk-panel">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
          <Search size={16} className="text-stone2"/>
          <input ref={inputRef} data-testid="cmdk-input" value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKeyDown}
                 placeholder="Search entities, create new, or jump to any section…"
                 className="flex-1 outline-none text-sm text-navy placeholder:text-stone2"/>
          <button data-testid="cmdk-close" onClick={() => setOpen(false)} className="text-stone2 hover:text-navy"><X size={14}/></button>
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="cmdk-results">
          {groups.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-stone2">
              {q.trim() ? "No matches. Try a name, slug, or 'new article'." : "Start typing to search entities, create new content, or navigate…"}
            </div>
          ) : (
            <ResultGroups items={groups} cursor={cursor} onPick={run} onHover={setCursor}/>
          )}
        </div>
        <div className="border-t border-line px-4 py-2 flex items-center gap-4 mono text-[9px] uppercase tracking-widest text-stone2">
          <span><kbd className="border border-line px-1.5 py-0.5">↑↓</kbd> navigate</span>
          <span><kbd className="border border-line px-1.5 py-0.5">↵</kbd> select</span>
          <span><kbd className="border border-line px-1.5 py-0.5">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

function ResultGroups({ items, cursor, onPick, onHover }) {
  // Cluster items in stable render order but preserve original cursor indexing.
  const ordered = items.map((it, idx) => ({ ...it, __idx: idx }));
  const entities = ordered.filter(x => x.kind === "entity");
  const creates = ordered.filter(x => x.kind === "create");
  const navs = ordered.filter(x => x.kind === "nav");
  const Row = ({ x, iconEl, right }) => (
    <button key={x.key} data-testid={`cmdk-item-${x.__idx}`}
            onMouseEnter={() => onHover(x.__idx)}
            onClick={() => onPick(x)}
            className={`w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm ${cursor === x.__idx ? "bg-clinical" : "hover:bg-clinical/60"}`}>
      <span className="text-stone2">{iconEl}</span>
      <span className="flex-1 min-w-0 truncate text-navy">{x.label}</span>
      {right}
      <ArrowRight size={12} className="text-stone2"/>
    </button>
  );

  return (
    <div>
      {entities.length > 0 && (
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-prestige px-4 pt-3 pb-1">Search results</div>
          {entities.map(x => (
            <Row key={x.key} x={x} iconEl={<Search size={14}/>}
                 right={<span className="mono text-[9px] uppercase tracking-widest text-stone2">{x.type.replace('_', ' ')}</span>}/>
          ))}
        </div>
      )}
      {creates.length > 0 && (
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-prestige px-4 pt-3 pb-1">Create</div>
          {creates.map(x => (<Row key={x.key} x={x} iconEl={<Plus size={14}/>} right={null}/>))}
        </div>
      )}
      {navs.length > 0 && (
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-prestige px-4 pt-3 pb-1">Navigate</div>
          {navs.map(x => { const I = x.icon || ArrowRight; return (<Row key={x.key} x={x} iconEl={<I size={14}/>} right={null}/>); })}
        </div>
      )}
    </div>
  );
}
